import { useQuery, useMutation } from "@tanstack/react-query";
import { useParams, useLocation, Link } from "wouter";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Separator } from "@/components/ui/separator";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { ArrowLeft, FileText, Download, Clock, CheckCircle, XCircle, Send, Eye, User, Mail, Calendar, Edit2, Loader2, AlertCircle, FileCheck, History, Building2, FileDown, UserPlus, Plus, Shield, Upload, Info, RefreshCw, CheckSquare, Phone, MessageCircle, Ban, Bell, ShieldCheck, Copy, FileArchive } from "lucide-react";
import { useLanguage } from "@/components/language-provider";
import { PdfPreview } from "@/components/PdfPreview";
import { useToast } from "@/hooks/use-toast";
import { format, formatDistanceToNow } from "date-fns";
import { ptBR, enUS } from "date-fns/locale";
import { useState, useRef, useEffect } from "react";
import { apiRequest, queryClient } from "@/lib/queryClient";
import { getApiUrl } from "@/lib/config";
import { StatusBadge } from "@/components/status-badge";
import { invalidateDocumentQueries, isDocumentActive, LIVE_INTERVAL_MS, LIVE_QUERY_OPTIONS, pollWhileDocumentPending } from "@/lib/live-documents";
import { GovBrOrderPanel } from "@/components/govbr-order-panel";
import { crmBadge, crmName, isOpsSource } from "@/lib/crm-source";

// WhatsApp SVG Icon component
const WhatsAppIcon = ({ className }: { className?: string }) => (
  <svg 
    className={className} 
    viewBox="0 0 24 24" 
    fill="currentColor"
    xmlns="http://www.w3.org/2000/svg"
  >
    <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413Z"/>
  </svg>
);

interface AuditEvent {
  id: string;
  eventType: string;
  description: string | null;
  signerEmail: string | null;
  signerName: string | null;
  ipAddress: string | null;
  createdAt: string;
}

interface AuditSigner {
  id: string;
  name: string;
  email: string;
  status: string;
  emailSentAt: string | null;
  viewedAt: string | null;
  signedAt: string | null;
}

interface AuditData {
  documentId: string;
  documentName: string;
  createdAt: string;
  sentAt: string | null;
  completedAt: string | null;
  company: {
    id: string;
    name: string;
  } | null;
  signers: AuditSigner[];
  events: AuditEvent[];
}

interface Signer {
  id: string;
  recipientName: string;
  recipientEmail: string;
  recipientPhone?: string | null;
  category?: string | null;
  status: string;
  createdAt: string;
  emailSentAt: string | null;
  lastEmailSentAt: string | null;
  viewedAt: string | null;
  signedAt: string | null;
  expiresAt: string | null;
  signedDocumentUrl: string | null;
  signatureMethod?: string | null;
  declineReason?: string | null;
  declinedAt?: string | null;
  manuallyCompletedAt?: string | null;
  token?: string | null;
  govBrUploadedDocUrl?: string | null;
  govBrUploadedAt?: string | null;
  govBrReviewedBy?: string | null;
  govBrReviewedAt?: string | null;
  // Queue position and request; for the gov.br signer in turn: since when and the last send of the file to them.
  order?: number;
  signatureRequestId?: string | null;
  isGovBrTurn?: boolean;
  govBrTurnSince?: string | null;
  govBrLastSentAt?: string | null;
}

interface DocumentDetail {
  id: string;
  name: string;
  type: string;
  status: string;
  fileUrl: string;
  signedFileUrl?: string | null;
  verificationCode?: string | null;
  previewUrl: string | null;
  fileSize: number;
  createdAt: string;
  signers: Signer[];
  crmSource?: string | null;
  crmEntityId?: string | null;
  crmEntityName?: string | null;
  crmRecordUrl?: string | null;
  crmRecordName?: string | null;
  crmSenderName?: string | null;
  sentByName?: string | null;
  isGovBrFlow?: boolean;
  /** Brought in by a bulk import: what the source system recorded. */
  import?: {
    importId: string;
    importName?: string | null;
    sourceSystem?: string | null;
    importedAt?: string | null;
    path?: string | null;
    externalId?: string | null;
    originalDate?: string | null;
    originalStatus?: string | null;
    signers?: { name?: string | null; email?: string | null; signedAt?: string | null }[];
  } | null;
}

export default function DocumentDetailPage() {
  const { id } = useParams<{ id: string }>();
  const [, setLocation] = useLocation();
  const { t, language } = useLanguage();
  // Dates in the language of the screen (date-fns defaults to English).
  const dfl = { locale: language === "pt" ? ptBR : enUS };
  const { toast } = useToast();
  
  const [editingSigner, setEditingSigner] = useState<Signer | null>(null);
  const [editName, setEditName] = useState("");
  const [editEmail, setEditEmail] = useState("");
  const [editPhone, setEditPhone] = useState("");
  const [selectedSigner, setSelectedSigner] = useState<Signer | null>(null);
  const [showAuditHistory, setShowAuditHistory] = useState(false);
  const [downloadingAuditPdf, setDownloadingAuditPdf] = useState(false);
  
  const [showManualComplete, setShowManualComplete] = useState(false);
  const [signerToComplete, setSignerToComplete] = useState<Signer | null>(null);
  const [signedDocFile, setSignedDocFile] = useState<File | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [showStatusUpdate, setShowStatusUpdate] = useState(false);
  const [newStatusValue, setNewStatusValue] = useState("");
  const [showCancelDocument, setShowCancelDocument] = useState(false);
  const [cancelDocReason, setCancelDocReason] = useState("");
  const [reminderCooldowns, setReminderCooldowns] = useState<Record<string, number>>({});
  const [whatsappCooldowns, setWhatsappCooldowns] = useState<Record<string, number>>({});
  // Resend buttons stay blocked for this long after the last send (initial send counts too:
  // the server's lastEmailSentAt/emailSentAt is used, so a page reload doesn't reset it).
  const REMINDER_COOLDOWN_MS = 10 * 60 * 1000;
  const [, setCooldownTick] = useState(0);
  useEffect(() => {
    const idt = setInterval(() => setCooldownTick((x) => x + 1), 30_000);
    return () => clearInterval(idt);
  }, []);
  const [shakingId, setShakingId] = useState<string | null>(null);

  const { data: document, isLoading } = useQuery<DocumentDetail>({
    queryKey: ["/api/documents", id, "details"],
    queryFn: async () => {
      const token = localStorage.getItem("token");
      if (!token) {
        // Not logged in - redirect to login with return URL
        window.location.href = `/login?redirect=${encodeURIComponent(`/documents/${id}`)}`;
        throw new Error("Redirecting to login...");
      }
      const res = await fetch(getApiUrl(`/api/documents/${id}/details`), {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.status === 401) {
        localStorage.removeItem("token");
        window.location.href = `/login?redirect=${encodeURIComponent(`/documents/${id}`)}`;
        throw new Error("Session expired");
      }
      if (!res.ok) throw new Error("Failed to load document");
      return res.json();
    },
    ...LIVE_QUERY_OPTIONS,
    // A signer signing in another tab shows up here within seconds; stops once the document is final.
    refetchInterval: pollWhileDocumentPending,
  });
  const docActive = isDocumentActive(document?.status);
  const detailsKey = ["/api/documents", id, "details"];
  // gov.br flow: the request whose signer has the file now, and its signers in queue order.
  const govRequestId = document?.isGovBrFlow ? document.signers.find((s) => s.isGovBrTurn)?.signatureRequestId ?? null : null;
  const govSigners = govRequestId ? document!.signers.filter((s) => s.signatureRequestId === govRequestId) : [];
  const govPrevious = (signer: Signer) => {
    const open = govSigners.filter((s) => s.status !== "signed" && s.status !== "declined");
    const i = open.findIndex((s) => s.id === signer.id);
    return i > 0 ? open[i - 1] : undefined;
  };

  const updateSignerMutation = useMutation({
    mutationFn: async ({ signerId, name, email, phone }: { signerId: string; name: string; email: string; phone?: string }) => {
      const res = await apiRequest("PUT", `/api/signatures/${signerId}/signer`, { recipientName: name, recipientEmail: email, recipientPhone: phone });
      if (!res.ok) {
        const data = await res.json();
        throw new Error(data.message || "Failed to update signer");
      }
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/documents", id, "details"] });
      setEditingSigner(null);
      toast({ title: t("common.success"), description: t("detail.signerUpdated") });
    },
    onError: (error: Error) => {
      toast({ title: t("common.error"), description: error.message, variant: "destructive" });
    },
  });

  const handleEditSigner = (signer: Signer) => {
    setEditingSigner(signer);
    setEditName(signer.recipientName);
    setEditEmail(signer.recipientEmail);
    setEditPhone(signer.recipientPhone || "");
  };

  const handleSaveEdit = () => {
    if (!editingSigner) return;
    updateSignerMutation.mutate({ signerId: editingSigner.id, name: editName, email: editEmail, phone: editPhone });
  };

  const manualCompleteMutation = useMutation({
    mutationFn: async ({ signatureId, file }: { signatureId: string; file: File | null }) => {
      const formData = new FormData();
      if (file) {
        formData.append("signedDocument", file);
      }
      const token = localStorage.getItem("token");
      const res = await fetch(getApiUrl(`/api/signatures/${signatureId}/manual-complete`), {
        method: "POST",
        headers: token ? { Authorization: `Bearer ${token}` } : {},
        body: formData,
      });
      if (!res.ok) {
        const data = await res.json();
        throw new Error(data.message || "Failed to complete signature");
      }
      return res.json();
    },
    onSuccess: () => {
      invalidateDocumentQueries(queryClient);
      setShowManualComplete(false);
      setSignerToComplete(null);
      setSignedDocFile(null);
      toast({ title: t("common.success"), description: t("detail.markedComplete") });
    },
    onError: (error: Error) => {
      toast({ title: t("common.error"), description: error.message, variant: "destructive" });
    },
  });

  const updateDocumentStatusMutation = useMutation({
    mutationFn: async (newStatus: string) => {
      const res = await apiRequest("PATCH", `/api/documents/${id}/status`, { status: newStatus });
      if (!res.ok) {
        const data = await res.json();
        throw new Error(data.message || "Failed to update status");
      }
      return res.json();
    },
    onSuccess: () => {
      invalidateDocumentQueries(queryClient);
      setShowStatusUpdate(false);
      setNewStatusValue("");
      toast({ title: t("common.success"), description: t("detail.statusUpdated") });
    },
    onError: (error: Error) => {
      toast({ title: t("common.error"), description: error.message, variant: "destructive" });
    },
  });

  const openManualComplete = (signer: Signer) => {
    setSignerToComplete(signer);
    setSignedDocFile(null);
    setShowManualComplete(true);
  };

  const handleManualComplete = () => {
    if (!signerToComplete) return;
    manualCompleteMutation.mutate({ signatureId: signerToComplete.id, file: signedDocFile });
  };

  const handleGovBrApprove = async (signer: Signer, approved: boolean) => {
    if (!approved) {
      const reason = window.prompt(language === "pt" ? "Motivo da rejeição:" : "Rejection reason:");
      if (reason === null) return;
      try {
        const res = await apiRequest("POST", `/api/sign/${signer.token}/approve-govbr`, { approved: false, reason });
        if (!res.ok) { const d = await res.json(); throw new Error(d.message); }
        queryClient.invalidateQueries({ queryKey: ["/api/documents", id, "details"] });
        toast({ title: "⚠️", description: language === "pt" ? "Assinatura rejeitada — signatário será notificado para reenviar" : "Signature rejected — signer will be notified to re-upload" });
      } catch (e: unknown) {
        toast({ title: language === "pt" ? "Erro" : "Error", description: e instanceof Error ? e.message : "Error", variant: "destructive" });
      }
      return;
    }
    try {
      const res = await apiRequest("POST", `/api/sign/${signer.token}/approve-govbr`, { approved: true });
      if (!res.ok) { const d = await res.json(); throw new Error(d.message); }
      queryClient.invalidateQueries({ queryKey: ["/api/documents", id, "details"] });
      toast({ title: "✅", description: language === "pt" ? "Assinatura GOV.BR aprovada!" : "GOV.BR signature approved!" });
    } catch (e: unknown) {
      toast({ title: language === "pt" ? "Erro" : "Error", description: e instanceof Error ? e.message : "Error", variant: "destructive" });
    }
  };

  const reminderMutation = useMutation({
    mutationFn: async (signerId: string) => {
      if (reminderCooldowns[signerId] && reminderCooldowns[signerId] > Date.now()) {
        throw new Error(t("errors.reminderWait"));
      }
      const res = await apiRequest("POST", `/api/signatures/${signerId}/reminder`, {});
      if (!res.ok) {
        const data = await res.json();
        throw new Error(data.message || "Failed to send reminder");
      }
      return { signerId, data: await res.json() };
    },
    onSuccess: (result: { signerId: string }) => {
      queryClient.invalidateQueries({ queryKey: ["/api/documents", id, "details"] });
      setShakingId(result.signerId);
      setTimeout(() => setShakingId(null), 2500);
      setReminderCooldowns(prev => ({ ...prev, [result.signerId]: Date.now() + REMINDER_COOLDOWN_MS }));
      toast({ title: t("common.success"), description: t("toasts.emailReminderSent") });
    },
    onError: (error: Error) => {
      toast({ title: t("common.error"), description: error.message, variant: "destructive" });
    },
  });

  const whatsappReminderMutation = useMutation({
    mutationFn: async (signerId: string) => {
      if (whatsappCooldowns[signerId] && whatsappCooldowns[signerId] > Date.now()) {
        throw new Error(t("errors.reminderWait"));
      }
      const res = await apiRequest("POST", `/api/signatures/${signerId}/reminder-whatsapp`, {});
      if (!res.ok) {
        const data = await res.json();
        throw new Error(data.message || "Failed to send WhatsApp reminder");
      }
      return { ...(await res.json()), signerId };
    },
    onSuccess: (result) => {
      queryClient.invalidateQueries({ queryKey: ["/api/documents", id, "details"] });
      toast({ title: t("common.success"), description: t("toasts.whatsappReminderSent") });
      setWhatsappCooldowns(prev => ({ ...prev, [result.signerId]: Date.now() + REMINDER_COOLDOWN_MS }));
    },
    onError: (error: Error) => {
      toast({ title: t("common.error"), description: error.message, variant: "destructive" });
    },
  });

  const cancelDocumentMutation = useMutation({
    mutationFn: async ({ reason }: { reason?: string }) => {
      const res = await apiRequest("POST", `/api/documents/${id}/cancel`, { reason });
      if (!res.ok) {
        const data = await res.json();
        throw new Error(data.message || "Failed to cancel document");
      }
      return res.json();
    },
    onSuccess: (result: { status?: string }) => {
      // The API now answers with the document's status: show it immediately, then refresh everything.
      const status = result?.status || "cancelled";
      queryClient.setQueryData(detailsKey, (old: DocumentDetail | undefined) => old ? { ...old, status } : old);
      invalidateDocumentQueries(queryClient);
      setShowCancelDocument(false);
      setCancelDocReason("");
      toast({ title: t("common.success"), description: t("toasts.documentCancelled") });
    },
    onError: (error: Error) => {
      toast({ title: t("common.error"), description: error.message, variant: "destructive" });
    },
  });



  const { data: auditData, isLoading: auditLoading } = useQuery<AuditData>({
    queryKey: ["/api/documents", id, "audit"],
    ...LIVE_QUERY_OPTIONS,
    refetchInterval: docActive ? LIVE_INTERVAL_MS : false,
    queryFn: async () => {
      const res = await fetch(getApiUrl(`/api/documents/${id}/audit`), {
        headers: { Authorization: `Bearer ${localStorage.getItem("token")}` },
      });
      if (!res.ok) throw new Error("Failed to load audit history");
      return res.json();
    },
    enabled: showAuditHistory,
  });

  const handleDownloadAuditPdf = async () => {
    if (!id) return;
    setDownloadingAuditPdf(true);
    try {
      const res = await fetch(getApiUrl(`/api/documents/${id}/audit/pdf`), {
        headers: { Authorization: `Bearer ${localStorage.getItem("token")}` },
      });
      if (!res.ok) throw new Error("Failed to generate audit PDF");
      
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = window.document.createElement("a");
      a.href = url;
      a.download = `${document?.name || "document"}_with_audit.pdf`;
      window.document.body.appendChild(a);
      a.click();
      window.document.body.removeChild(a);
      URL.revokeObjectURL(url);
      
      toast({ title: t("common.success"), description: t("detail.auditDownloaded") });
    } catch (error: any) {
      toast({ title: t("common.error"), description: error.message || "Failed to download audit PDF", variant: "destructive" });
    } finally {
      setDownloadingAuditPdf(false);
    }
  };

  const getEventTypeLabel = (eventType: string): string => {
    const labels: Record<string, string> = {
      document_created: "Document Created",
      document_uploaded: "Document Uploaded",
      document_imported: "Document Imported",
      document_replaced: "Document Replaced",
      document_deleted: "Document Deleted",
      signature_request_created: "Signature Request Created",
      signature_request_sent: "Request Sent",
      signature_request_cancelled: "Request Cancelled",
      signer_added: "Signer Added",
      signer_updated: "Signer Updated",
      signer_removed: "Signer Removed",
      document_viewed: "Document Viewed",
      signature_viewed: "Signature Link Opened",
      signing_page_opened: "Signing Page Opened",
      signature_applied: "Signature Applied",
      signature_declined: "Signature Declined",
      document_completed: "Document Completed",
      audit_pdf_generated: "Audit PDF Generated",
      signature_request_expired: "Request Expired",
      signature_expired: "Signature Expired",
      copy_sent_to_signer: "Signed Copy Sent to Signer",
      signing_link_resent: "Signing Link Resent",
      document_sent_to_signer: "Document Sent to Signer",
      reminder_sent: "Reminder Sent",
      signing_order_changed: "Signing Order Changed",
    };
    // The API sends the enum name lowercased ("signatureapplied"); labels are keyed in snake_case.
    const compact = eventType.toLowerCase().replace(/_/g, "");
    const name = Object.keys(labels).find(k => k.replace(/_/g, "") === compact) ?? eventType;
    const key = `auditEvent.${name}`;
    const translated = t(key);
    if (translated !== key) return translated;
    return labels[name] || eventType.replace(/_/g, " ").replace(/\b\w/g, c => c.toUpperCase());
  };

  const getStatusBadge = (rawStatus: string, viewedAt: string | null) => {
    // Recipients keep "pending" in the database when the request is cancelled/expired/declined:
    // the document outcome is what the sender needs to see on each unsigned signer.
    const docOutcome = (document?.status || "").toLowerCase();
    const status = rawStatus !== "signed" && rawStatus !== "declined" && ["cancelled", "expired", "declined"].includes(docOutcome)
      ? docOutcome
      : rawStatus;
    const isViewed = !!viewedAt && status === "pending";
    const variants: Record<string, { variant: "default" | "secondary" | "destructive" | "outline"; icon: typeof CheckCircle }> = {
      pending: { variant: "secondary", icon: Clock },
      viewed: { variant: "outline", icon: Eye },
      signed: { variant: "default", icon: CheckCircle },
      declined: { variant: "destructive", icon: XCircle },
      expired: { variant: "outline", icon: AlertCircle },
      cancelled: { variant: "destructive", icon: Ban },
    };
    const displayStatus = isViewed ? "viewed" : status;
    const config = variants[displayStatus] || variants.pending;
    const Icon = config.icon;
    return (
      <Badge variant={config.variant} className="gap-1">
        <Icon className="h-3 w-3" />
        {isViewed ? t("status.viewed") : (t(`status.${status}`) === `status.${status}` ? status.charAt(0).toUpperCase() + status.slice(1) : t(`status.${status}`))}
      </Badge>
    );
  };

  if (isLoading) {
    return (
      <div className="container mx-auto p-3 sm:p-6 max-w-6xl">
        <Skeleton className="h-10 w-48 mb-6" />
        <div className="grid gap-4 sm:gap-6 lg:grid-cols-2">
          <Skeleton className="h-[500px]" />
          <Skeleton className="h-[500px]" />
        </div>
      </div>
    );
  }

  if (!document) {
    return (
      <div className="container mx-auto p-3 sm:p-6 max-w-6xl text-center py-12 sm:py-20">
        <FileText className="h-16 w-16 mx-auto text-muted-foreground mb-4" />
        <h2 className="text-xl font-semibold mb-2">{t("detail.notFound")}</h2>
        <Button onClick={() => setLocation("/documents")}>
          <ArrowLeft className="mr-2 h-4 w-4" /> {t("detail.backToDocuments")}
        </Button>
      </div>
    );
  }

  // Prefer signed preview when available.  Look for a signed document URL on
  // any signer or on the document itself.  If none found, fall back to
  // previewUrl or fileUrl.
  const previewSrc = (() => {
    if (!document) return null as any;
    // Look at signers for a signed document URL
    const signerSigned = document.signers?.find(s => s.signedDocumentUrl);
    if (signerSigned && signerSigned.signedDocumentUrl) {
      return signerSigned.signedDocumentUrl;
    }
    if (document.signedFileUrl) return document.signedFileUrl;
    return document.previewUrl || document.fileUrl;
  })();


  return (
    <div className="container mx-auto p-3 sm:p-6 max-w-6xl">
      <div className="flex items-center gap-2 sm:gap-4 mb-4 sm:mb-6">
        <Button variant="ghost" size="icon" onClick={() => setLocation("/documents")} data-testid="button-back">
          <ArrowLeft className="h-5 w-5" />
        </Button>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2">
            <h1 className="text-lg sm:text-2xl font-bold truncate" data-testid="text-document-name">{document.name}</h1>
            {document.isGovBrFlow && (
              <span style={{display:"inline-flex",alignItems:"center",gap:4,background:"#059669",color:"#fff",fontSize:11,fontWeight:600,padding:"2px 8px",borderRadius:6,whiteSpace:"nowrap",flexShrink:0}}>
                {"🏛️"} {language === "pt" ? "Fluxo GOV.BR" : "GOV.BR Flow"}
              </span>
            )}
          </div>
          <p className="text-xs sm:text-sm text-muted-foreground">
            {document.import
              ? `${t("detail.importedOn")} ${format(new Date(document.import.importedAt || document.createdAt), "PPP", dfl)}`
              : `${language === "pt" ? "Enviado em" : "Uploaded on"} ${format(new Date(document.createdAt), "PPP", dfl)}`}
          </p>
        </div>
        <div className="flex items-center gap-1 sm:gap-2 flex-shrink-0">
          <Button 
            variant="outline"
            size="sm"
            onClick={() => setShowAuditHistory(true)}
            data-testid="button-audit-history"
          >
            <History className="h-4 w-4" /> <span className="hidden sm:inline ml-1">{t("ui.audit_history")}</span>
          </Button>
{/* Only show Update Status button if document is not completed AND has at least one manual-verify signer */}
          {document.status !== "completed" && document.signers?.some(s => s.signatureMethod === "govbr") && (
            <Button 
              variant="ghost" 
              size="sm"
              onClick={() => {
                setNewStatusValue(document.status);
                setShowStatusUpdate(true);
              }}
              data-testid="button-update-status"
            >
              <RefreshCw className="h-4 w-4 mr-2" /> {t("ui.update_status")}
            </Button>
          )}
          <StatusBadge status={document.status} className="text-sm px-3 py-1" />
        </div>
      </div>

      {/* Signer Details Dialog */}
      <Dialog open={!!selectedSigner} onOpenChange={(open) => !open && setSelectedSigner(null)}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>{t("ui.signer_details")}</DialogTitle>
          </DialogHeader>
          {selectedSigner && (
            <div className="space-y-4">
              <div className="flex items-center gap-4">
                <div className="h-12 w-12 rounded-full bg-primary/10 flex items-center justify-center">
                  <User className="h-6 w-6 text-primary" />
                </div>
                <div>
                  <p className="font-semibold text-lg">{selectedSigner.recipientName}</p>
                  <p className="text-sm text-muted-foreground">{selectedSigner.recipientEmail}</p>
                </div>
              </div>
              
              <div className="space-y-2 p-4 rounded-lg bg-muted/50">
                <div className="flex justify-between">
                  <span className="text-muted-foreground">{t("ui.status")}</span>
                  {getStatusBadge(selectedSigner.status, selectedSigner.viewedAt)}
                </div>
                {selectedSigner.category && (
                  <div className="flex justify-between text-sm">
                    <span className="text-muted-foreground">{t("detail.category")}</span>
                    <Badge variant="outline" className="text-xs">{selectedSigner.category}</Badge>
                  </div>
                )}
                {selectedSigner.emailSentAt && (
                  <div className="flex justify-between text-sm">
                    <span className="text-muted-foreground">{t("detail.sent")}</span>
                    <span>{format(new Date(selectedSigner.emailSentAt), "PP, HH:mm", dfl)}</span>
                  </div>
                )}
                {selectedSigner.viewedAt && (
                  <div className="flex justify-between text-sm">
                    <span className="text-muted-foreground">{t("detail.viewed")}</span>
                    <span>{format(new Date(selectedSigner.viewedAt), "PP, HH:mm", dfl)}</span>
                  </div>
                )}
                {selectedSigner.signedAt && (
                  <div className="flex justify-between text-sm">
                    <span className="text-muted-foreground">{t("detail.signed")}</span>
                    <span>{format(new Date(selectedSigner.signedAt), "PP, HH:mm", dfl)}</span>
                  </div>
                )}
                {selectedSigner.expiresAt && (
                  <div className="flex justify-between text-sm">
                    <span className="text-muted-foreground">{t("detail.expires")}</span>
                    <span>{format(new Date(selectedSigner.expiresAt), "PP, HH:mm", dfl)}</span>
                  </div>
                )}
              </div>
              
              {selectedSigner.status === "pending" && (
                <Button 
                  variant="outline" 
                  className="w-full"
                  onClick={() => {
                    setSelectedSigner(null);
                    handleEditSigner(selectedSigner);
                  }}
                >
                  <Edit2 className="h-4 w-4 mr-2" /> {t("detail.editSigner")}
                </Button>
              )}
              
              {selectedSigner.signedDocumentUrl && (
                <a href={selectedSigner.signedDocumentUrl} target="_blank" rel="noopener noreferrer" className="block">
                  <Button variant="default" className="w-full">
                    <Download className="h-4 w-4 mr-2" /> {t("detail.downloadSigned")}
                  </Button>
                </a>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Edit Signer Dialog */}
      <Dialog open={!!editingSigner} onOpenChange={(open) => !open && setEditingSigner(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{t("ui.edit_signer_information")}</DialogTitle>
            <DialogDescription>{t("ui.update_the_signer_s_name_email_and_phone_number")}</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="edit-name">{t("ui.name")}</Label>
              <Input
                id="edit-name"
                value={editName}
                onChange={(e) => setEditName(e.target.value)}
                placeholder={t("ui.signer_name")}
                data-testid="input-edit-signer-name"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="edit-email">{t("ui.email")}</Label>
              <Input
                id="edit-email"
                type="email"
                value={editEmail}
                onChange={(e) => setEditEmail(e.target.value)}
                placeholder="signer@example.com"
                data-testid="input-edit-signer-email"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="edit-phone" className="flex items-center gap-2">
                <Phone className="h-4 w-4 text-green-600" />
                WhatsApp Number (optional)
              </Label>
              <Input
                id="edit-phone"
                type="tel"
                value={editPhone}
                onChange={(e) => setEditPhone(e.target.value)}
                placeholder="+55 11 99999-9999"
                data-testid="input-edit-signer-phone"
              />
              <p className="text-xs text-muted-foreground">{t("detail.phoneHint")}</p>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditingSigner(null)}>{t("ui.cancel")}</Button>
            <Button 
              onClick={handleSaveEdit}
              disabled={updateSignerMutation.isPending || !editName || !editEmail}
              data-testid="button-save-signer"
            >
              {updateSignerMutation.isPending && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
              {t("ui.save_changes")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Audit History Dialog */}
      <Dialog open={showAuditHistory} onOpenChange={setShowAuditHistory}>
        <DialogContent className="max-w-2xl max-h-[85vh] flex flex-col">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <History className="h-5 w-5" />
              {t("ui.audit_history")}
            </DialogTitle>
            <DialogDescription>
              {t("ui.complete_history_and_timeline_of_this_document")}
            </DialogDescription>
          </DialogHeader>

          {auditLoading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
            </div>
          ) : auditData ? (
            <ScrollArea className="flex-1 -mx-6 px-6">
              <div className="space-y-6 pb-4">
                {/* Document Info */}
                <div className="p-4 rounded-lg bg-muted/50 space-y-2">
                  <div className="flex items-center gap-2 mb-2">
                    <FileText className="h-4 w-4 text-muted-foreground" />
                    <span className="font-medium">{t("detail.details")}</span>
                  </div>
                  <div className="grid grid-cols-2 gap-2 text-sm">
                    <div>
                      <span className="text-muted-foreground">{t("detail.name")}</span>
                      <p className="font-medium">{auditData.documentName}</p>
                    </div>
                    <div>
                      <span className="text-muted-foreground">{t("detail.created")}</span>
                      <p className="font-medium">{format(new Date(auditData.createdAt), "PPP, p", dfl)}</p>
                    </div>
                    {document.sentByName && (
                      <div>
                        <span className="text-muted-foreground">{t("detail.sentByLabel")}</span>
                        <p className="font-medium">{document.sentByName}</p>
                      </div>
                    )}
                    {auditData.sentAt && (
                      <div>
                        <span className="text-muted-foreground">{t("detail.firstSent")}</span>
                        <p className="font-medium">{format(new Date(auditData.sentAt), "PPP, p", dfl)}</p>
                      </div>
                    )}
                    {auditData.completedAt && (
                      <div>
                        <span className="text-muted-foreground">{t("detail.completed")}</span>
                        <p className="font-medium">{format(new Date(auditData.completedAt), "PPP, p", dfl)}</p>
                      </div>
                    )}
                    {auditData.company && (
                      <div className="col-span-2">
                        <span className="text-muted-foreground flex items-center gap-1">
                          <Building2 className="h-3 w-3" /> {t("detail.company")}
                        </span>
                        <p className="font-medium">{auditData.company.name}</p>
                      </div>
                    )}
                    {document.crmSource && (
                      <div className="col-span-2 pt-2 border-t">
                        <span className="text-muted-foreground text-xs flex items-center gap-1 mb-1">
                          <Building2 className="h-3 w-3" /> {crmName(document.crmSource)}
                        </span>
                        <p className="text-xs">
                          {document.crmEntityName}
                          {document.sentByName && <span> — {t("detail.sentBy")} <strong>{document.sentByName}</strong></span>}
                          {document.crmRecordUrl && (
                            <a href={document.crmRecordUrl} target="_blank" rel="noopener noreferrer" className="text-primary hover:underline ml-2">{t("detail.open")} →</a>
                          )}
                        </p>
                      </div>
                    )}
                  </div>
                </div>

                {/* Signers Summary */}
                {auditData.signers.length > 0 && (
                  <div className="space-y-3">
                    <h4 className="font-medium flex items-center gap-2">
                      <User className="h-4 w-4" /> {t("detail.signersTimeline")}
                    </h4>
                    {auditData.signers.map((signer) => (
                      <div key={signer.id} className="p-3 rounded-lg border bg-card">
                        <div className="flex items-center justify-between gap-4 mb-2">
                          <div>
                            <p className="font-medium">{signer.name}</p>
                            <p className="text-sm text-muted-foreground">{signer.email}</p>
                          </div>
                          <Badge variant={signer.status === "signed" ? "default" : "secondary"}>
                            {signer.status}
                          </Badge>
                        </div>
                        <div className="space-y-1 text-xs text-muted-foreground">
                          {signer.emailSentAt && (
                            <div className="flex items-center gap-2">
                              <Send className="h-3 w-3" />
                              <span>Sent: {format(new Date(signer.emailSentAt), "PP, HH:mm", dfl)}</span>
                            </div>
                          )}
                          {signer.viewedAt && (
                            <div className="flex items-center gap-2">
                              <Eye className="h-3 w-3" />
                              <span>Viewed: {format(new Date(signer.viewedAt), "PP, HH:mm", dfl)}</span>
                            </div>
                          )}
                          {signer.signedAt && (
                            <div className="flex items-center gap-2">
                              <CheckCircle className="h-3 w-3" />
                              <span>Signed: {format(new Date(signer.signedAt), "PP, HH:mm", dfl)}</span>
                            </div>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                )}

                <Separator />

                {/* Event Log */}
                <div className="space-y-3">
                  <h4 className="font-medium flex items-center gap-2">
                    <Calendar className="h-4 w-4" /> {t("detail.eventLog")}
                  </h4>
                  <div className="space-y-2">
                    {auditData.events.map((event) => (
                      <div key={event.id} className="flex gap-3 text-sm">
                        <div className="w-2 h-2 mt-2 rounded-full bg-primary flex-shrink-0" />
                        <div className="flex-1 min-w-0">
                          <p className="font-medium">{getEventTypeLabel(event.eventType)}</p>
                          {event.description && (
                            <p className="text-muted-foreground text-xs">{event.description}</p>
                          )}
                          {event.signerName && (
                            <p className="text-muted-foreground text-xs">
                              Signer: {event.signerName} ({event.signerEmail})
                            </p>
                          )}
                          <p className="text-xs text-muted-foreground mt-1">
                            {format(new Date(event.createdAt), "PP, HH:mm:ss", dfl)}
                            {event.ipAddress && ` • IP: ${event.ipAddress}`}
                          </p>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </ScrollArea>
          ) : (
            <div className="text-center py-12 text-muted-foreground">
              <AlertCircle className="h-12 w-12 mx-auto mb-4 opacity-50" />
              <p>{t("ui.no_audit_data_available")}</p>
            </div>
          )}

          <DialogFooter className="mt-4 flex-col sm:flex-row gap-2">
            <Button variant="outline" onClick={() => setShowAuditHistory(false)} data-testid="button-close-audit">
              {t("ui.close")}
            </Button>
            {document.signedFileUrl && (
              <Button 
                variant="outline"
                onClick={() => {
                  window.open(document.signedFileUrl!, '_blank', 'noopener,noreferrer');
                }}
                data-testid="button-download-signed"
              >
                <Download className="h-4 w-4 mr-2" />
                {t("ui.download_signed_document")}
              </Button>
            )}
            <Button 
              onClick={handleDownloadAuditPdf}
              disabled={downloadingAuditPdf}
              data-testid="button-download-audit-pdf"
            >
              {downloadingAuditPdf ? (
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
              ) : (
                <FileDown className="h-4 w-4 mr-2" />
              )}
              {document.signedFileUrl ? "Download Complete Package" : "Download with Audit Trail"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Manual Status Update Dialog */}
      <Dialog open={showStatusUpdate} onOpenChange={setShowStatusUpdate}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{t("ui.update_document_status")}</DialogTitle>
            <DialogDescription>
              {t("ui.manually_change_the_document_status_this_will_be")}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>{t("ui.current_status")}</Label>
              <Badge variant="secondary">{document.status}</Badge>
            </div>
            <div className="space-y-2">
              <Label htmlFor="new-status">{t("ui.new_status")}</Label>
              <Select value={newStatusValue} onValueChange={setNewStatusValue}>
                <SelectTrigger id="new-status" data-testid="select-new-status">
                  <SelectValue placeholder={t("ui.select_new_status")} />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="draft">{t("ui.draft")}</SelectItem>
                  <SelectItem value="pending">{t("ui.pending")}</SelectItem>
                  <SelectItem value="completed">{t("ui.completed")}</SelectItem>
                  <SelectItem value="concluded">{t("ui.concluded")}</SelectItem>
                  <SelectItem value="cancelled">{t("ui.cancelled")}</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowStatusUpdate(false)}>
              {t("ui.cancel")}
            </Button>
            <Button
              onClick={() => {
                if (newStatusValue) {
                  updateDocumentStatusMutation.mutate(newStatusValue);
                }
              }}
              disabled={!newStatusValue || newStatusValue === document.status || updateDocumentStatusMutation.isPending}
              data-testid="button-confirm-status-update"
            >
              {updateDocumentStatusMutation.isPending ? (
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
              ) : (
                <CheckSquare className="h-4 w-4 mr-2" />
              )}
              {t("ui.update_status")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Manual Signature Completion Dialog */}
      <Dialog open={showManualComplete} onOpenChange={(open) => {
        if (!open) {
          setShowManualComplete(false);
          setSignerToComplete(null);
          setSignedDocFile(null);
        }
      }}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{language === "pt" ? "Confirmar Assinatura Manual" : "Confirm Manual Signature"}</DialogTitle>
            <DialogDescription>
              {language === "pt" 
                ? "Marcar a assinatura deste signatário como concluída. Opcionalmente, envie o documento assinado."
                : "Mark this signer's signature as complete. You can optionally upload the signed document."}
            </DialogDescription>
          </DialogHeader>
          {signerToComplete && (
            <div className="space-y-4 py-4">
              <div className="p-3 rounded-lg bg-muted">
                <p className="font-medium">{signerToComplete.recipientName}</p>
                <p className="text-sm text-muted-foreground">{signerToComplete.recipientEmail}</p>
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="signed-doc">{t("ui.signed_document_optional")}</Label>
                <p className="text-xs text-muted-foreground">
                  {t("ui.upload_the_externally_signed_pdf_if_available")}
                </p>
                <input
                  ref={fileInputRef}
                  type="file"
                  id="signed-doc"
                  accept=".pdf"
                  className="hidden"
                  onChange={(e) => setSignedDocFile(e.target.files?.[0] || null)}
                />
                <div className="flex items-center gap-2">
                  <Button
                    variant="outline"
                    onClick={() => fileInputRef.current?.click()}
                    data-testid="button-select-signed-doc"
                  >
                    <Upload className="h-4 w-4 mr-2" />
                    {signedDocFile ? "Change File" : "Select File"}
                  </Button>
                  {signedDocFile && (
                    <span className="text-sm text-muted-foreground truncate max-w-[200px]">
                      {signedDocFile.name}
                    </span>
                  )}
                </div>
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowManualComplete(false)}>
              {t("ui.cancel")}
            </Button>
            <Button 
              onClick={handleManualComplete}
              disabled={manualCompleteMutation.isPending}
              data-testid="button-confirm-complete"
            >
              {manualCompleteMutation.isPending ? (
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
              ) : (
                <CheckSquare className="h-4 w-4 mr-2" />
              )}
              {t("ui.confirm_complete")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <div className="grid gap-4 sm:gap-6 lg:grid-cols-2">
        <Card>
          <CardHeader className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 sm:gap-4">
            <div>
              <CardTitle className="flex items-center gap-2 text-base sm:text-lg">
                <Eye className="h-5 w-5" />
                {t("ui.document_preview")}
                {document.crmSource && (
                  <Badge variant="outline" className="text-[10px] px-1.5 py-0 bg-green-50 text-green-700 border-green-200 dark:bg-green-950 dark:text-green-400 dark:border-green-800">
                    <Building2 className="h-2.5 w-2.5 mr-0.5 inline" />{crmBadge(document.crmSource)}
                  </Badge>
                )}
              </CardTitle>
              <CardDescription>{(document.fileSize / 1024).toFixed(1)} KB</CardDescription>
            </div>
            <div className="flex gap-2">
              <a href={document.fileUrl} target="_blank" rel="noopener noreferrer">
                <Button variant="outline" size="sm" data-testid="button-download-original">
                  <Download className="h-4 w-4" /> <span className="hidden sm:inline ml-1">{t("detail.original")}</span>
                </Button>
              </a>
              {document.status === "completed" && document.signers.some(s => s.signedDocumentUrl) && (
                <a href={document.signers.find(s => s.signedDocumentUrl)?.signedDocumentUrl || ""} target="_blank" rel="noopener noreferrer">
                  <Button variant="default" size="sm" data-testid="button-download-signed">
                    <FileCheck className="h-4 w-4" /> <span className="hidden sm:inline ml-1">{t("detail.signedVersion")}</span>
                  </Button>
                </a>
              )}
            </div>
          </CardHeader>
          <CardContent>
            <div className="bg-muted rounded-lg" style={{ maxHeight: "600px" }}>
              {previewSrc ? (
                <div className="w-full">
                <PdfPreview src={previewSrc as string} className="w-full p-2" maxHeight={500} />
                </div>
              ) : (
                <div className="h-[300px] sm:h-[500px] flex items-center justify-center">
                  <div className="text-center">
                    <FileText className="h-16 w-16 mx-auto text-muted-foreground mb-4" />
                    <p className="text-muted-foreground">{t("detail.previewUnavailable")}</p>
                  </div>
                </div>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Public verification: code + QR printed on the signed PDF */}
        {document.verificationCode && (
          <Card data-testid="card-verification">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <ShieldCheck className="h-5 w-5" />
                {language === "pt" ? "Verificação pública" : "Public verification"}
              </CardTitle>
              <CardDescription>
                {language === "pt"
                  ? "Qualquer pessoa confirma a autenticidade com o código ou lendo o QR impresso no rodapé do PDF assinado."
                  : "Anyone can confirm authenticity with the code or by scanning the QR printed at the bottom of the signed PDF."}
              </CardDescription>
            </CardHeader>
            <CardContent className="flex flex-wrap items-center gap-5">
              <img src={getApiUrl(`/api/verify/${document.verificationCode}/qr.svg`)} alt="QR" className="h-28 w-28 rounded-lg border bg-white p-1" data-testid="verification-qr" />
              <div className="min-w-0 space-y-2">
                <p className="font-mono text-lg font-semibold text-primary" data-testid="verification-code">{document.verificationCode}</p>
                <a href={`https://fillsign.com.br/verificar/${document.verificationCode}`} target="_blank" rel="noopener noreferrer" className="block break-all text-sm text-primary hover:underline">
                  fillsign.com.br/verificar/{document.verificationCode}
                </a>
                <Button variant="outline" size="sm" onClick={() => { void navigator.clipboard?.writeText(`https://fillsign.com.br/verificar/${document.verificationCode}`); toast({ title: language === "pt" ? "Link copiado" : "Link copied" }); }} data-testid="button-copy-verification">
                  <Copy className="mr-2 h-4 w-4" />{language === "pt" ? "Copiar link" : "Copy link"}
                </Button>
              </div>
            </CardContent>
          </Card>
        )}

        {/* CRM Integration Info */}
        {document.crmSource && (
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Building2 className="h-5 w-5" />
                {isOpsSource(document.crmSource) ? "FillSign Ops" : t("ui.dynamics_365_integration")}
              </CardTitle>
              <CardDescription>This document was created from {document.crmSource}</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-2 gap-4 text-sm">
                <div>
                  <span className="text-muted-foreground">{t("detail.entity")}</span>
                  <p className="font-medium">{document.crmEntityName || "—"}</p>
                </div>
                <div>
                  <span className="text-muted-foreground">{t("detail.sentBy")}</span>
                  <p className="font-medium">{document.sentByName || "—"}</p>
                </div>
                {document.crmRecordUrl && (
                  <div className="col-span-2">
                    <span className="text-muted-foreground">{t("detail.recordUrl")}</span>
                    <p>
                      <a href={document.crmRecordUrl} target="_blank" rel="noopener noreferrer" className="text-primary hover:underline text-xs break-all">
                        {document.crmRecordUrl}
                      </a>
                    </p>
                  </div>
                )}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Bulk import: what the source system recorded about this file */}
        {document.import && (
          <Card data-testid="card-import">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <FileArchive className="h-5 w-5" />
                {t("detail.importTitle")}
              </CardTitle>
              <CardDescription>
                {t("detail.importDesc")
                  .replace("{source}", document.import.sourceSystem || t("detail.importSourceUnknown"))
                  .replace("{name}", document.import.importName || "")}
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-1 gap-3 text-sm sm:grid-cols-2">
                {document.import.originalDate && (
                  <div>
                    <span className="text-muted-foreground">{t("detail.importOriginalDate")}</span>
                    <p className="font-medium">{format(new Date(document.import.originalDate), "PPp", dfl)}</p>
                  </div>
                )}
                {document.import.originalStatus && (
                  <div>
                    <span className="text-muted-foreground">{t("detail.importOriginalStatus")}</span>
                    <p className="font-medium">{document.import.originalStatus}</p>
                  </div>
                )}
                {document.import.externalId && (
                  <div>
                    <span className="text-muted-foreground">{t("detail.importExternalId")}</span>
                    <p className="font-mono font-medium">{document.import.externalId}</p>
                  </div>
                )}
                {document.import.path && (
                  <div className="sm:col-span-2">
                    <span className="text-muted-foreground">{t("detail.importPath")}</span>
                    <p className="break-all font-mono text-xs">{document.import.path}</p>
                  </div>
                )}
              </div>
              {!!document.import.signers?.length && (
                <div>
                  <p className="mb-2 text-sm text-muted-foreground">{t("detail.importSigners")}</p>
                  <ul className="space-y-1.5">
                    {document.import.signers.map((s, i) => (
                      <li key={i} className="flex flex-wrap items-center justify-between gap-2 rounded-md border px-3 py-2 text-sm">
                        <span className="min-w-0">
                          <span className="font-medium">{s.name || s.email}</span>
                          {s.name && s.email && <span className="text-muted-foreground"> · {s.email}</span>}
                        </span>
                        {s.signedAt && <span className="text-xs text-muted-foreground">{format(new Date(s.signedAt), "PPp", dfl)}</span>}
                      </li>
                    ))}
                  </ul>
                </div>
              )}
              <Link href={`/documents?import=${document.import.importId}`}>
                <Button variant="outline" size="sm" data-testid="button-import-all">{t("detail.importSeeAll")}</Button>
              </Link>
            </CardContent>
          </Card>
        )}

        <Card>
          <CardHeader className="flex flex-row items-center justify-between">
            <div>
              <CardTitle className="flex items-center gap-2">
                <User className="h-5 w-5" />
                {t("docs.signers")} ({document.signers.length})
              </CardTitle>
              <CardDescription>{t("ui.track_the_signing_progress")}</CardDescription>
            </div>
            {docActive && document.signers.length > 0 && (
              <Button
                variant="outline"
                size="sm"
                className="text-destructive hover:text-destructive hover:bg-destructive/10 border-destructive/30 gap-1.5"
                onClick={() => setShowCancelDocument(true)}
              >
                <Ban className="h-3.5 w-3.5" />
                {t("ui.cancel_document")}
              </Button>
            )}
          </CardHeader>
          <CardContent>
            {docActive && govRequestId && govSigners.filter((s) => s.status !== "signed" && s.status !== "declined").length > 1 && (
              <GovBrOrderPanel
                requestId={govRequestId}
                signers={govSigners}
                language={language}
                onSaved={() => invalidateDocumentQueries(queryClient)}
              />
            )}
            {document.signers.length === 0 ? (
              <div className="text-center py-12 text-muted-foreground">
                <User className="h-12 w-12 mx-auto mb-4 opacity-50" />
                <p>{t("ui.no_signers_yet")}</p>
                <p className="text-sm">{t("detail.notSent")}</p>
              </div>
            ) : (
              <div className="space-y-4">
                {document.signers.map((signer) => (
                  <div
                    key={signer.id}
                    className="p-4 rounded-lg border bg-card cursor-pointer hover-elevate"
                    onClick={() => setSelectedSigner(signer)}
                    data-testid={`card-signer-${signer.id}`}
                  >
                    <div className="flex flex-col sm:flex-row sm:items-start justify-between gap-2 sm:gap-4 mb-3">
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 flex-wrap">
                          <p className="font-medium" data-testid={`text-signer-name-${signer.id}`}>
                            {signer.recipientName}
                          </p>
                          {signer.signatureMethod === "govbr" && (
                            <Badge 
                              variant="outline" 
                              className="text-xs bg-green-50 dark:bg-green-900/20 border-green-300 dark:border-green-700 text-green-700 dark:text-green-300"
                              data-testid={`badge-govbr-${signer.id}`}
                            >
                              <Shield className="h-3 w-3 mr-1" />
                              {document?.isGovBrFlow ? "gov.br" : "Nome+CPF"}
                            </Badge>
                          )}
                          {signer.category && (
                            <Badge variant="outline" className="text-xs">
                              {signer.category}
                            </Badge>
                          )}
                        </div>
                        <p className="text-sm text-muted-foreground flex items-center gap-1 truncate">
                          <Mail className="h-3 w-3 flex-shrink-0" />
                          {signer.recipientEmail}
                        </p>
                        {signer.recipientPhone && (
                          <p className="text-sm text-muted-foreground flex items-center gap-1 truncate">
                            <Phone className="h-3 w-3 flex-shrink-0 text-green-600" />
                            <span className="text-green-600">{signer.recipientPhone}</span>
                          </p>
                        )}
                      </div>
                      <div className="flex items-center gap-2 flex-shrink-0">
                        {getStatusBadge(signer.status, signer.viewedAt)}
                        {signer.status === "pending" && docActive && (
                          <TooltipProvider>
                            {(!document?.isGovBrFlow || (signer.isGovBrTurn && !signer.govBrUploadedDocUrl)) && (() => {
                              // gov.br: only the signer who has the file now gets reminders; the last send to them starts the wait.
                              const lastSent = document?.isGovBrFlow ? signer.govBrLastSentAt : (signer.lastEmailSentAt || signer.emailSentAt);
                              const serverUntil = lastSent ? new Date(lastSent).getTime() + REMINDER_COOLDOWN_MS : 0;
                              const coolUntil = Math.max(reminderCooldowns[signer.id] || 0, serverUntil);
                              const isCooling = coolUntil > Date.now();
                              const isShaking = shakingId === signer.id;
                              const coolMinLeft = isCooling ? Math.max(1, Math.ceil((coolUntil - Date.now()) / 60000)) : 0;
                              return (
                                <Tooltip>
                                  <TooltipTrigger asChild>
                                    <Button
                                      variant="ghost"
                                      size="icon"
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        if (!isCooling) reminderMutation.mutate(signer.id);
                                      }}
                                      disabled={reminderMutation.isPending}
                                      aria-disabled={isCooling}
                                      className={isCooling ? "cursor-not-allowed" : ""}
                                      style={{
                                        opacity: isCooling ? 0.35 : 1,
                                        filter: isCooling ? "grayscale(1)" : "none",
                                        animation: isShaking ? "bell-shake 0.4s ease-in-out 0s 6" : "none",
                                      }}
                                      data-testid={`button-reminder-${signer.id}`}
                                    >
                                      {reminderMutation.isPending ? (
                                        <Loader2 className="h-4 w-4 animate-spin" />
                                      ) : (
                                        <Bell className="h-4 w-4" />
                                      )}
                                    </Button>
                                  </TooltipTrigger>
                                  <TooltipContent>
                                    <p>{isCooling ? `${t("tooltips.reminderJustSent")} ${coolMinLeft} min.` : t("tooltips.sendEmailReminder")}</p>
                                  </TooltipContent>
                                </Tooltip>
                              );
                            })()}
                            {signer.recipientPhone && (!document?.isGovBrFlow || (signer.isGovBrTurn && !signer.govBrUploadedDocUrl)) && (
                              (() => {
                                const waCooling = !!(whatsappCooldowns[signer.id] && whatsappCooldowns[signer.id] > Date.now());
                                const waCoolMin = waCooling ? Math.ceil((whatsappCooldowns[signer.id] - Date.now()) / 60000) : 0;
                                return (
                              <Tooltip>
                                <TooltipTrigger asChild>
                                  <Button
                                    variant="outline"
                                    size="sm"
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      if (!waCooling) whatsappReminderMutation.mutate(signer.id);
                                    }}
                                    disabled={whatsappReminderMutation.isPending}
                                    aria-disabled={waCooling}
                                    className={`text-green-600 hover:text-green-700 hover:bg-green-50 border-green-300 gap-1.5 ${waCooling ? "cursor-not-allowed" : ""}`}
                                    style={{
                                      opacity: waCooling ? 0.35 : 1,
                                      filter: waCooling ? "grayscale(1)" : "none",
                                    }}
                                    data-testid={`button-whatsapp-reminder-${signer.id}`}
                                  >
                                    {whatsappReminderMutation.isPending ? (
                                      <Loader2 className="h-4 w-4 animate-spin" />
                                    ) : (
                                      <>
                                        <WhatsAppIcon className="h-4 w-4" />
                                        <span className="text-xs font-medium">{waCooling ? `${waCoolMin}min` : "WhatsApp"}</span>
                                      </>
                                    )}
                                  </Button>
                                </TooltipTrigger>
                                <TooltipContent>
                                  <p>{waCooling ? `${t("tooltips.whatsappReminderJustSent")} ${waCoolMin} min.` : t("tooltips.sendWhatsAppReminder")}</p>
                                </TooltipContent>
                              </Tooltip>
                                );
                              })()
                            )}
                            <Tooltip>
                              <TooltipTrigger asChild>
                                <Button
                                  variant="ghost"
                                  size="icon"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    handleEditSigner(signer);
                                  }}
                                  data-testid={`button-edit-signer-${signer.id}`}
                                >
                                  <Edit2 className="h-4 w-4" />
                                </Button>
                              </TooltipTrigger>
                              <TooltipContent>
                                <p>{t("tooltips.editSigner")}</p>
                              </TooltipContent>
                            </Tooltip>
                          </TooltipProvider>
                        )}
                      </div>
                    </div>
                    
                    {document?.isGovBrFlow && (
                      <div className="mb-3 p-3 rounded-lg border" data-testid={`panel-govbr-${signer.id}`}
                        style={{ background: signer.govBrUploadedDocUrl ? "#fefce8" : "#eff6ff", 
                                 borderColor: signer.govBrUploadedDocUrl ? "#fde68a" : "#bfdbfe" }}>
                        <div className="flex items-start gap-2 mb-2">
                          <Shield className="h-4 w-4 text-green-600 mt-0.5 flex-shrink-0" />
                          <div className="text-xs flex-1">
                            <p className="font-medium mb-1">
                              {language === "pt" ? "Assinatura Digital GOV.BR" : "GOV.BR Digital Signature"}
                            </p>
                            
                            {/* Pending: the signer in turn has the file; the next ones wait for theirs */}
                            {signer.status === "pending" && !signer.govBrUploadedDocUrl && (() => {
                              const waitingTurn = !!govRequestId && signer.signatureRequestId === govRequestId && !signer.isGovBrTurn;
                              const previous = waitingTurn ? govPrevious(signer) : undefined;
                              return (
                                <div className="flex items-start gap-2 text-blue-700" data-testid={`govbr-state-${signer.id}`}>
                                  <Clock className="h-3 w-3 mt-0.5 flex-shrink-0" />
                                  <span>{waitingTurn
                                    ? (language === "pt"
                                      ? `Aguardando a vez: recebe o documento depois que ${previous?.recipientName ?? "a pessoa anterior"} devolver o arquivo assinado`
                                      : `Waiting for their turn: gets the document after ${previous?.recipientName ?? "the previous signer"} returns the signed file`)
                                    : (language === "pt" ? "Aguardando envio do documento assinado pelo signatário" : "Waiting for signer to upload signed document")
                                      + (signer.govBrTurnSince
                                        ? (language === "pt" ? ` (com o documento desde ${new Date(signer.govBrTurnSince).toLocaleString("pt-BR")})` : ` (has had the document since ${new Date(signer.govBrTurnSince).toLocaleString("en-US")})`)
                                        : "")}</span>
                                </div>
                              );
                            })()}

                            {/* Uploaded - waiting for review */}
                            {signer.govBrUploadedDocUrl && signer.status !== "signed" && (
                              <div>
                                <div className="flex items-center gap-2 text-amber-700 mb-2">
                                  <AlertCircle className="h-3 w-3" />
                                  <span className="font-medium">{language === "pt" ? "Documento enviado — aguardando revisão" : "Document uploaded — waiting for review"}</span>
                                </div>
                                {signer.govBrUploadedAt && (
                                  <p className="text-muted-foreground">{language === "pt" ? "Enviado em" : "Uploaded at"}: {new Date(signer.govBrUploadedAt).toLocaleString(language === "pt" ? "pt-BR" : "en-US")}</p>
                                )}
                                <div className="flex gap-2 mt-3">
                                  <a href={signer.govBrUploadedDocUrl} target="_blank" rel="noopener noreferrer">
                                    <Button variant="outline" size="sm">
                                      <FileText className="h-3 w-3 mr-1" />
                                      {language === "pt" ? "Ver documento" : "View document"}
                                    </Button>
                                  </a>
                                  <Button size="sm" className="bg-green-600 hover:bg-green-700"
                                    onClick={(e) => { e.stopPropagation(); handleGovBrApprove(signer, true); }}>
                                    <CheckCircle className="h-3 w-3 mr-1" />
                                    {language === "pt" ? "Aprovar" : "Approve"}
                                  </Button>
                                  <Button size="sm" variant="destructive"
                                    onClick={(e) => { e.stopPropagation(); handleGovBrApprove(signer, false); }}>
                                    <XCircle className="h-3 w-3 mr-1" />
                                    {language === "pt" ? "Rejeitar" : "Reject"}
                                  </Button>
                                </div>
                              </div>
                            )}

                            {/* Signed - approved */}
                            {signer.status === "signed" && signer.govBrReviewedAt && (
                              <div className="flex items-center gap-2 text-green-700">
                                <CheckCircle className="h-3 w-3" />
                                <span>{signer.govBrReviewedBy?.startsWith("auto:")
                                  ? (language === "pt" ? "Assinatura conferida no arquivo devolvido" : "Signature checked in the returned file")
                                  : (language === "pt" ? "Aprovado" : "Approved")} — {new Date(signer.govBrReviewedAt).toLocaleString(language === "pt" ? "pt-BR" : "en-US")}</span>
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                    )}

                    {/* Non-GOV.BR manual complete (legacy) */}
                    {signer.signatureMethod === "govbr" && !document?.isGovBrFlow && signer.status === "pending" && (
                      <div 
                        className="mb-3 p-3 rounded-lg bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800"
                        data-testid={`panel-govbr-info-${signer.id}`}
                      >
                        <div className="flex items-start gap-2">
                          <Info className="h-4 w-4 text-blue-600 dark:text-blue-400 mt-0.5 flex-shrink-0" />
                          <div className="text-xs text-blue-800 dark:text-blue-200">
                            <p className="font-medium mb-1">{language === "pt" ? "Verificação por Nome e CPF" : "Name + CPF Verification"}</p>
                            <p>{language === "pt" ? "Este assinante utiliza verificação por Nome e CPF." : "This signer uses Name + CPF verification."}</p>
                          </div>
                        </div>
                        <div className="mt-3 flex justify-end">
                          <Button size="sm" onClick={(e) => { e.stopPropagation(); openManualComplete(signer); }} data-testid={`button-mark-complete-${signer.id}`}>
                            <CheckSquare className="h-4 w-4 mr-2" />
                            {language === "pt" ? "Confirmar Assinatura" : "Confirm Signature"}
                          </Button>
                        </div>
                      </div>
                    )}
                    
                    {signer.status === "declined" && signer.declineReason && (
                      <div className="mb-3 p-3 rounded-lg bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800">
                        <div className="flex items-start gap-2">
                          <XCircle className="h-4 w-4 text-red-600 dark:text-red-400 mt-0.5 flex-shrink-0" />
                          <div className="text-xs text-red-800 dark:text-red-200">
                            <p className="font-medium mb-1">{t("detail.declineReason")}</p>
                            <p>"{signer.declineReason}"</p>
                          </div>
                        </div>
                      </div>
                    )}
                    <div className="space-y-1 text-xs text-muted-foreground">
                      <div className="flex items-center gap-1">
                        <Send className="h-3 w-3 flex-shrink-0" />
                        <span>{t("ui.sent")} </span>
                        <span>
                          {(() => {
                            // gov.br: what counts is the file reaching the signer in turn; the next ones have not received it yet.
                            const govOpen = !!govRequestId && signer.signatureRequestId === govRequestId && signer.status !== "signed";
                            const sentAt = govOpen ? (signer.isGovBrTurn ? signer.govBrLastSentAt : null) : signer.emailSentAt;
                            if (sentAt) return `${format(new Date(sentAt), "MMM d, yyyy h:mm a")} (${formatDistanceToNow(new Date(sentAt), { addSuffix: true, ...dfl })})`;
                            if (govOpen && !signer.isGovBrTurn) return language === "pt" ? "ainda não (aguardando a vez)" : "not yet (waiting for their turn)";
                            return "Not sent";
                          })()}
                        </span>
                      </div>
                      {signer.viewedAt && (
                        <div className="flex items-center gap-1">
                          <Eye className="h-3 w-3 flex-shrink-0" />
                          <span>{t("ui.viewed")} </span>
                          <span>{format(new Date(signer.viewedAt), "MMM d, yyyy h:mm a")} ({formatDistanceToNow(new Date(signer.viewedAt), { addSuffix: true, ...dfl })})</span>
                        </div>
                      )}
                      {signer.signedAt && (
                        <div className="flex items-center gap-1">
                          <CheckCircle className="h-3 w-3 flex-shrink-0" />
                          <span>{t("ui.signed")} </span>
                          <span>{format(new Date(signer.signedAt), "MMM d, yyyy h:mm a")} ({formatDistanceToNow(new Date(signer.signedAt), { addSuffix: true, ...dfl })})</span>
                        </div>
                      )}
                      {!signer.signedAt && !signer.viewedAt && (
                        <div className="flex items-center gap-1">
                          <Clock className="h-3 w-3 flex-shrink-0" />
                          <span>{t("ui.awaiting_signature")}</span>
                        </div>
                      )}
                    </div>
                    {signer.expiresAt && signer.status === "pending" && (
                      <div className="mt-2 text-xs text-muted-foreground flex items-center gap-1">
                        <AlertCircle className="h-3 w-3" />
                        Expires {formatDistanceToNow(new Date(signer.expiresAt), { addSuffix: true, ...dfl })}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
            
          </CardContent>
        </Card>
      </div>

      {/* Cancel Document Dialog */}
      <Dialog open={showCancelDocument} onOpenChange={(open) => !open && setShowCancelDocument(false)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Ban className="h-5 w-5 text-destructive" />
              {t("ui.cancel_document")}
            </DialogTitle>
            <DialogDescription>
              Are you sure you want to cancel this document? All pending signers will be notified by email that the signature request has been cancelled.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="cancel-doc-reason">{t("ui.reason")}</Label>
              <Textarea
                id="cancel-doc-reason"
                placeholder={t("ui.please_provide_a_reason_for_cancellation")}
                value={cancelDocReason}
                onChange={(e) => setCancelDocReason(e.target.value)}
                className="min-h-[80px]"
                data-testid="input-cancel-doc-reason"
              />
            </div>
          </div>
          <DialogFooter className="gap-2">
            <Button
              variant="outline"
              onClick={() => { setShowCancelDocument(false); setCancelDocReason(""); }}
            >
              {t("ui.keep_document")}
            </Button>
            <Button
              variant="destructive"
              onClick={() => {
                cancelDocumentMutation.mutate({ reason: cancelDocReason || undefined });
              }}
              disabled={cancelDocumentMutation.isPending || !cancelDocReason.trim()}
              data-testid="button-confirm-cancel-doc"
            >
              {cancelDocumentMutation.isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              <Ban className="mr-2 h-4 w-4" />
              {t("ui.cancel_document")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Bell shake animation */}
      <style>{`@keyframes bell-shake { 0%{transform:rotate(0)} 15%{transform:rotate(14deg)} 30%{transform:rotate(-12deg)} 45%{transform:rotate(10deg)} 60%{transform:rotate(-8deg)} 75%{transform:rotate(4deg)} 100%{transform:rotate(0)} }`}</style>
    </div>
  );
}
